import { useMemo, useState, type ReactNode } from 'react';
import { AlertTriangle, Check, ImageOff, Loader2, Palette, Save, Sun, Trash2, Trees } from 'lucide-react';
import * as api from '../worlds.api';
import { toast, useWorldsStore } from '../worlds.store';
import { BIOME_NAME_RE, COLOR_RE, DEFAULT_COLOR, fmtDate, toSnakeName } from '../constants';
import type { Biome, BiomeInput, Level } from '../worlds.types';
import { Modal } from './Modal';
import { confirmAction } from './ConfirmDialog';
import { ColorChip } from './ui';
import { ColorField } from './ColorField';
import { NamePair } from './NamePair';
import styles from '../../LootboxPage.module.scss';
import ws from '../WorldsPage.module.scss';
import lm from '../levelsMarkers.module.scss';

// ============================================================
// Biome (catalogue des marqueurs) : création, modification, suppression
// ============================================================
// Nom technique immuable, déduit du nom FR à la création tant qu'il n'est pas saisi à la main.
// `level` réserve le biome à un niveau (vide = tous). Mêmes bornes que le satellite
// (BiomeService, WorldsValidation) : noms de 100 caractères, coefficient solaire de 0 à 100.
// Un biome encore utilisé par des marqueurs n'est pas supprimable (le satellite refuse, 409).
// ============================================================

const NAME_MAX = 100;
const SOLAR_MAX = 100;

/**
 * Icône d'un biome (URL externe, optionnelle) : masquée si elle ne charge pas. `keepSpace` garde
 * un emplacement vide de même taille (alignement des listes).
 */
export function BiomeIcon({ url, size = 18, keepSpace = false, title }: {
  url: string | null | undefined;
  size?: number;
  keepSpace?: boolean;
  title?: string;
}) {
  const [failed, setFailed] = useState<string | null>(null);
  if (!url || failed === url) {
    return keepSpace ? <span className={lm.biomeImgSpacer} style={{ width: size, height: size }} aria-hidden /> : null;
  }
  return (
    <img className={lm.biomeImg} src={url} alt="" width={size} height={size} loading="lazy" draggable={false}
      title={title} onError={() => setFailed(url)} />
  );
}

/** « 1,5 » ou « 1.5 » → 1.5 ; vide ou illisible : NaN. */
function parseNumber(v: string): number {
  const t = v.trim().replace(',', '.');
  return t === '' ? NaN : Number(t);
}

/** Coefficient → effet lisible (« +50 % », « neutre », « aucun rendement »). */
function solarEffect(coef: number): string {
  if (coef === 1) return 'neutre';
  if (coef === 0) return 'aucun rendement';
  const pct = Math.round((coef - 1) * 100);
  return `${pct > 0 ? '+' : ''}${pct.toLocaleString('fr-FR')} % de rendement`;
}

export function BiomeModal({ biome, levels, onClose, onSaved, onDeleted }: {
  biome: Biome | null;
  levels: Level[];
  onClose: () => void;
  onSaved: (b: Biome) => void;
  onDeleted: (name: string) => void;
}) {
  const biomes = useWorldsStore((s) => s.biomes);
  const markers = useWorldsStore((s) => s.markers);
  const [name, setName] = useState(biome?.name ?? '');
  const [nameEdited, setNameEdited] = useState(false);
  const [displayNameFr, setFr] = useState(biome?.displayNameFr ?? '');
  const [displayNameEn, setEn] = useState(biome?.displayNameEn ?? '');
  const [color, setColor] = useState(biome?.color?.toUpperCase() ?? DEFAULT_COLOR);
  const [iconUrl, setIconUrl] = useState(biome?.iconUrl ?? '');
  const [enabled, setEnabled] = useState(biome?.enabled ?? true);
  const [catalogOnly, setCatalogOnly] = useState(biome?.catalogOnly ?? false);
  const [solar, setSolar] = useState(String(biome?.solarCoefficient ?? 1).replace('.', ','));
  const [level, setLevel] = useState(biome?.level ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tried, setTried] = useState(false);
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // Compteur du satellite (tous niveaux) ou, s'il est en retard sur une pose récente, marqueurs
  // du niveau affiché : le plus grand des deux.
  const used = useMemo(() => {
    if (!biome) return 0;
    const local = markers.reduce((n, m) => n + (m.biomeName === biome.name ? 1 : 0), 0);
    return Math.max(biome.markerCount, local);
  }, [biome, markers]);

  const taken = !biome && !!name && biomes.some((b) => b.name === name);
  const solarNum = parseNumber(solar);
  const trimmedIcon = iconUrl.trim();
  const errors = {
    name: biome ? null
      : !name ? 'Requis'
        : !BIOME_NAME_RE.test(name) ? 'Minuscules, chiffres et _, commence par une lettre (64 au plus)'
          : taken ? 'Nom déjà pris' : null,
    displayNameFr: !displayNameFr.trim() ? 'Requis' : null,
    displayNameEn: !displayNameEn.trim() ? 'Requis' : null,
    color: !COLOR_RE.test(color) ? 'Format #RRGGBB' : null,
    iconUrl: trimmedIcon && (!/^https?:\/\//.test(trimmedIcon) || trimmedIcon.length > 500)
      ? 'URL http(s) de 500 caractères au plus' : null,
    solar: !Number.isFinite(solarNum) || solarNum < 0 || solarNum > SOLAR_MAX
      ? `Nombre entre 0 et ${SOLAR_MAX}` : null,
  };
  type Field = keyof typeof errors;
  const valid = Object.values(errors).every((e) => !e);
  const show = (f: Field) => (tried || touched[f] ? errors[f] : null);
  const touch = (f: Field) => setTouched((t) => (t[f] ? t : { ...t, [f]: true }));

  const onFr = (v: string) => {
    setFr(v);
    // Nom technique déduit du nom FR tant qu'il n'a pas été saisi à la main (création seulement).
    if (!biome && !nameEdited) setName(toSnakeName(v));
  };
  const onName = (v: string) => {
    const n = v.toLowerCase().replace(/[^a-z0-9_]/g, '');
    setName(n);
    // Champ vidé : on repasse en déduction automatique.
    setNameEdited(n !== '');
    if (n === '') setName(toSnakeName(displayNameFr));
  };

  const save = async () => {
    setTried(true);
    if (!valid || busy) return;
    const body: BiomeInput = {
      displayNameFr: displayNameFr.trim(),
      displayNameEn: displayNameEn.trim(),
      color: color.toUpperCase(),
      iconUrl: trimmedIcon || null,
      enabled,
      catalogOnly,
      solarCoefficient: solarNum,
      level: level || null,
    };
    setBusy(true);
    setError(null);
    let res;
    if (biome) {
      const diff: BiomeInput = {};
      (Object.keys(body) as (keyof BiomeInput)[]).forEach((k) => {
        const cur = body[k];
        const old = k === 'color' ? biome.color.toUpperCase() : (biome as unknown as Record<string, unknown>)[k];
        if (cur !== old) (diff as Record<string, unknown>)[k] = cur;
      });
      if (Object.keys(diff).length === 0) { setBusy(false); onClose(); return; }
      res = await api.patchBiome(biome.name, diff);
    } else {
      res = await api.createBiome({ ...body, name });
    }
    setBusy(false);
    if (!res.data) { setError(res.error ?? 'Enregistrement en échec'); return; }
    toast.success(biome ? `Biome ${res.data.displayNameFr || res.data.name} mis à jour` : `Biome ${res.data.displayNameFr || res.data.name} créé`);
    onSaved(res.data);
    onClose();
  };

  const remove = async () => {
    if (!biome || used > 0) return;
    const ok = await confirmAction({
      title: 'Supprimer le biome',
      message: <p>Supprimer le biome <strong>{biome.displayNameFr || biome.name}</strong> (<code>{biome.name}</code>) ?</p>,
      details: 'Aucun marqueur ne l\'utilise. Il disparaît de la palette de tous les niveaux.',
    });
    if (!ok) return;
    setBusy(true);
    setError(null);
    const res = await api.deleteBiome(biome.name);
    setBusy(false);
    // 204 : pas de corps, donc ni data ni error. 409 si un marqueur a été posé entre-temps.
    if (res.error) { setError(res.error); return; }
    toast.success(`Biome ${biome.displayNameFr || biome.name} supprimé`);
    onDeleted(biome.name);
    onClose();
  };

  const err = (f: Field) => { const e = show(f); return e ? <span className={ws.fieldError}>{e}</span> : null; };
  const chipColor = COLOR_RE.test(color) ? color : DEFAULT_COLOR;
  const levelMissing = !!level && !levels.some((l) => l.name === level);
  const deleteTitle = used > 0
    ? `Utilisé par ${used.toLocaleString('fr-FR')} marqueur(s) : supprime-les ou change leur biome d'abord.`
    : 'Supprimer ce biome';

  let nameHint: ReactNode;
  if (biome) nameHint = <span className={ws.fieldHint}>Immuable : référencé par les marqueurs.</span>;
  else if (show('name')) nameHint = err('name');
  else if (taken) nameHint = <span className={ws.fieldError}>Nom déjà pris</span>;
  else if (name && BIOME_NAME_RE.test(name)) {
    nameHint = <span className={`${lm.nameStatus} ${ws.textOk}`}><Check size={12} aria-hidden /> Disponible{nameEdited ? '' : ' (déduit du nom FR)'}</span>;
  } else nameHint = <span className={ws.fieldHint}>Déduit du nom FR : minuscules, chiffres et _.</span>;

  return (
    <Modal title={biome ? `Modifier le biome ${biome.displayNameFr || biome.name}` : 'Nouveau biome'}
      onClose={onClose} busy={busy} onSubmit={() => void save()}
      icon={<ColorChip color={chipColor} size={24} />}
      subtitle={biome
        ? <>{used.toLocaleString('fr-FR')} marqueur(s) · modifié le {fmtDate(biome.updatedAt)}{biome.updatedBy ? ` par ${biome.updatedBy}` : ''}</>
        : 'Catalogue des marqueurs de biomes : nom, couleur, icône et effets en jeu.'}
      footer={(
        <>
          {error && <span className={ws.footError}><AlertTriangle size={13} /> {error}</span>}
          {biome && (
            <span className={`${ws.actWrap} ${error ? '' : lm.footDanger}`} title={deleteTitle}>
              <button type="button" className={`${styles.secondaryBtn} ${ws.dangerBtn}`} onClick={() => void remove()}
                disabled={busy || used > 0} aria-label={deleteTitle}>
                <Trash2 size={14} /> Supprimer
              </button>
            </span>
          )}
          <button type="button" className={styles.secondaryBtn} onClick={onClose} disabled={busy}>Annuler</button>
          <button type="submit" className={styles.primaryBtn} disabled={busy || (tried && !valid)}>
            {busy ? <Loader2 size={14} className={styles.spin} /> : <Save size={14} />} {biome ? 'Enregistrer' : 'Créer'}
          </button>
        </>
      )}>
      <section className={ws.formSection}>
        <h3 className={ws.formSectionTitle}><Trees size={14} aria-hidden /> Identité</h3>
        <NamePair label="Nom affiché" required copyFromFr disabled={busy}
          fr={{
            value: displayNameFr, onChange: onFr, maxLength: NAME_MAX, placeholder: 'Ex. Forêt enchantée',
            error: show('displayNameFr'), autoFocus: true,
          }}
          en={{
            value: displayNameEn, onChange: setEn, maxLength: NAME_MAX, placeholder: 'Ex. Enchanted forest',
            error: show('displayNameEn'),
          }} />
        <label className={styles.field} style={{ minWidth: 0 }}>
          <span className={styles.label}>Nom technique{!biome && <span className={ws.req}> *</span>}</span>
          <input className={`${styles.input} ${ws.mono}`} value={name} disabled={!!biome || busy} maxLength={64}
            placeholder="foret_enchantee" spellCheck={false} aria-invalid={!!show('name') || taken || undefined}
            onChange={(e) => onName(e.target.value)} onBlur={() => touch('name')} />
          {nameHint}
        </label>
      </section>

      <section className={ws.formSection}>
        <h3 className={ws.formSectionTitle}><Palette size={14} aria-hidden /> Apparence</h3>
        <ColorField value={color} onChange={setColor} disabled={busy} error={show('color')} />
        <div className={lm.iconRow}>
          <label className={styles.field} style={{ minWidth: 0, flex: 1 }}>
            <span className={styles.label}>Icône (URL)</span>
            <input className={`${styles.input} ${ws.mono}`} value={iconUrl} maxLength={500} disabled={busy} spellCheck={false}
              placeholder="https://…/desert.png" aria-invalid={!!show('iconUrl') || undefined}
              onChange={(e) => setIconUrl(e.target.value)} onBlur={() => touch('iconUrl')} />
            {err('iconUrl') ?? (
              <span className={ws.fieldHint}>Image http(s) hébergée hors du panel, affichée sur les marqueurs et dans la palette. Vide : pastille seule.</span>
            )}
          </label>
          <span className={lm.iconPreview} title={trimmedIcon ? 'Aperçu de l\'icône' : 'Aucune icône'}>
            {trimmedIcon && !errors.iconUrl
              ? <BiomeIconPreview url={trimmedIcon} />
              : <ImageOff size={16} aria-hidden />}
          </span>
        </div>
      </section>

      <section className={ws.formSection}>
        <h3 className={ws.formSectionTitle}><Sun size={14} aria-hidden /> En jeu</h3>
        <div className={ws.formGrid}>
          <label className={styles.field} style={{ minWidth: 0 }}>
            <span className={styles.label}>Coefficient solaire</span>
            <input className={styles.input} inputMode="decimal" value={solar} disabled={busy} aria-invalid={!!show('solar') || undefined}
              onChange={(e) => setSolar(e.target.value)} onBlur={() => touch('solar')} />
            {err('solar') ?? (
              <span className={ws.fieldHint}>
                {Number.isFinite(solarNum) ? `= ${solarEffect(solarNum)}. ` : ''}Multiplicateur des panneaux solaires (1 = neutre, 1,5 = +50 %), de 0 à {SOLAR_MAX}.
              </span>
            )}
          </label>
          <label className={styles.field} style={{ minWidth: 0 }}>
            <span className={styles.label}>Niveau</span>
            <select className={styles.input} value={level} disabled={busy} onChange={(e) => setLevel(e.target.value)}>
              <option value="">Tous les niveaux</option>
              {levelMissing && <option value={level}>{level} (niveau absent)</option>}
              {levels.map((l) => <option key={l.name} value={l.name}>{l.title} ({l.name})</option>)}
            </select>
            <span className={ws.fieldHint}>
              {level ? 'Réservé à ce niveau : la palette des autres niveaux ne le propose pas.' : 'Proposé dans la palette de tous les niveaux.'}
            </span>
          </label>
        </div>
        <div className={lm.checkBlock}>
          <label className={ws.checkLine}>
            <input type="checkbox" checked={enabled} disabled={busy} onChange={(e) => setEnabled(e.target.checked)} /> Activé
          </label>
          <span className={lm.checkHint}>
            {enabled ? 'Décoché, les marqueurs de ce biome sont ignorés et il ne peut plus être placé.' : 'Désactivé : les marqueurs de ce biome sont ignorés, il ne peut plus être placé.'}
          </span>
        </div>
        <div className={lm.checkBlock}>
          <label className={ws.checkLine}>
            <input type="checkbox" checked={catalogOnly} disabled={busy} onChange={(e) => setCatalogOnly(e.target.checked)} /> Catalogue seulement
          </label>
          <span className={lm.checkHint}>Pseudo-biome de dimension (ex. Lune, Edora) repris de NG Java : au catalogue, jamais généré.</span>
        </div>
      </section>
    </Modal>
  );
}

/** Aperçu 32 px de l'icône saisie, avec repli si l'image ne charge pas. */
function BiomeIconPreview({ url }: { url: string }) {
  const [failed, setFailed] = useState<string | null>(null);
  if (failed === url) return <span title="Image introuvable à cette URL"><ImageOff size={16} className={ws.textWarn} aria-hidden /></span>;
  return <img className={lm.biomeImg} src={url} alt="" width={32} height={32} onError={() => setFailed(url)} />;
}
